import { useEffect, useState } from "react";
import TenantLayout from "@/pages/tenant/components/TenantLayout";
import { getTenantDashboard } from "@/utils/tenantApi";

export default function TenantDashboardPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getTenantDashboard();
      setData(res);
    } catch (err) {
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <TenantLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
        </div>
      </TenantLayout>
    );
  }

  const stats = data?.stats || {};
  const plan = data?.plan;
  const recentSites = data?.recent_sites || [];

  return (
    <TenantLayout>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">代理商控制台</h2>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">我的站点</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50">
                <i className="ri-global-line text-blue-500" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.site_count || 0}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <span>当前套餐限额: {plan?.site_limit || '无限制'}</span>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">子用户</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-50">
                <i className="ri-team-line text-orange-500" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.user_count || 0}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <span>可创建子用户数</span>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">信息总数</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-green-50">
                <i className="ri-file-list-3-line text-green-500" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.listing_count || 0}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <span>今日新增: {stats.today_listings || 0}</span>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">当前套餐</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-purple-50">
                <i className="ri-vip-diamond-line text-purple-500" />
              </div>
            </div>
            <p className="text-lg font-bold text-gray-800">{plan?.name || '未购买套餐'}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <span>¥{plan?.amount || 0}</span>
            </div>
          </div>
        </div>

        {/* Recent Sites */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-gray-800">最近创建的站点</h3>
            <a href="/tenant/sites" className="text-sm text-blue-500 hover:text-blue-600">查看全部</a>
          </div>
          
          {recentSites.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentSites.map((site: any) => (
                <div key={site.id} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-800">{site.name}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      site.status === 'active' ? 'bg-green-50 text-green-600' :
                      site.status === 'stopped' ? 'bg-yellow-50 text-yellow-600' :
                      'bg-red-50 text-red-600'
                    }`}>
                      {site.status === 'active' ? '运行中' : site.status === 'stopped' ? '已停用' : '已过期'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">{site.subdomain}</div>
                  <div className="text-xs text-gray-400 mt-2">{site.created_at?.split(' ')[0]}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-global-line text-3xl mb-2" />
              <p className="text-sm">暂无站点</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">快捷操作</h3>
          <div className="flex flex-wrap gap-3">
            <a href="/tenant/sites" className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-600 text-sm font-medium rounded-xl hover:bg-orange-100 transition-colors">
              <i className="ri-add-line" />
              新建站点
            </a>
            <a href="/tenant/users" className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-xl hover:bg-blue-100 transition-colors">
              <i className="ri-user-add-line" />
              用户管理
            </a>
            <a href="/tenant/plans" className="flex items-center gap-2 px-4 py-2.5 bg-green-50 text-green-600 text-sm font-medium rounded-xl hover:bg-green-100 transition-colors">
              <i className="ri-settings-3-line" />
              套餐管理
            </a>
            <a href="/tenant/account" className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors">
              <i className="ri-user-settings-line" />
              账户设置
            </a>
          </div>
        </div>
      </div>
    </TenantLayout>
  );
}
